// eslint-disable-next-line no-restricted-imports
import MaterialAvatar, {
  AvatarProps as MuiAvatarProps,
} from '@material-ui/core/Avatar';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';

import { stringToColor, extractInitials } from './utils';

/** @public */
export type AvatarClassKey = 'avatar' | 'avatarText' | 'colorDefault';

interface StyleProps {
  backgroundColor?: string;
}

const useStyles = makeStyles<Theme, StyleProps>(
  theme => ({
    avatar: {
      backgroundColor: (props: { backgroundColor?: string }) =>
        props.backgroundColor,
    },
    colorDefault: {
      backgroundColor: theme.palette.blueGrey[600],
    },
    avatarText: {
      color: ({ backgroundColor }) =>
        theme.palette.getContrastText(
          backgroundColor || theme.palette.blueGrey[600],
        ),
      fontSize: 'inherit',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
  }),
  { name: 'WbAvatar' },
);

/**
 * Properties for {@link WbAvatar}.
 *
 * @public
 */
export interface WbAvatarProps
  extends Omit<MuiAvatarProps, 'src' | 'alt' | 'classes'> {
  /**
   * A display name used to generate initials as fallback when no picture is provided.
   */
  displayName: string;
  /**
   * URL to avatar image source.
   */
  picture?: string;
  /**
   * Custom styles applied to avatar.
   */
  classes?: { [key in AvatarClassKey]?: string };
  /**
   * When true, shows a single initial instead of two.
   */
  singleInitial?: boolean;
}

/**
 * Component rendering an avatar with two-letter initials fallback.
 */
export function WbAvatar(props: WbAvatarProps) {
  const {
    displayName,
    picture,
    classes: customClasses,
    className,
    singleInitial,
    ...restProps
  } = props;

  const getBackgroundColor = () => {
    if (!displayName) return undefined;
    return stringToColor(displayName);
  };

  const classes = useStyles(
    !picture ? { backgroundColor: getBackgroundColor() } : {},
  );

  const avatarClassNames = clsx(
    customClasses?.avatar,
    classes.avatar,

    className,
  );
  const avatarTextClassNames = clsx(
    customClasses?.avatarText,
    classes.avatarText,
  );
  const avatarColorDefaultClassNames = clsx(
    customClasses?.colorDefault,
    classes.colorDefault,
  );

  return (
    <MaterialAvatar
      {...restProps}
      alt={displayName}
      src={picture}
      className={avatarClassNames}
      classes={{ colorDefault: avatarColorDefaultClassNames }}
      {...(!picture &&
        displayName && { role: 'img', 'aria-label': displayName })}
    >
      {!picture && displayName ? (
        <Typography
          component="span"
          className={avatarTextClassNames}
          aria-hidden
        >
          {extractInitials(displayName, singleInitial ? 1 : 2)}
        </Typography>
      ) : null}
    </MaterialAvatar>
  );
}
